import { AsyncPipe, NgClass, NgStyle, UpperCasePipe } from '@angular/common';
import {
    ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, Signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { ProductPricesComponent } from '../product-prices/product-prices.component';
import { ProductPromotionDatesComponent } from '../product-promotion-dates/product-promotion-dates.component';
import { ProductViewConfig } from '../product-view/models/product-view-config.model';
import { CategoryConfiguration } from '../product-view/models/product-view-template.model';
import { ProductViewColorSelectorComponent } from '../product-view-color-selector/product-view-color-selector.component';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { getCmsConfigValue, getLanguageisRTL } from '@core/infrastructure/state/core.selectors';
import { MocacoAttributesPublicConstants, MocacoAttributesPublicModels } from '@features/products/mocaco-attributes';
import { ProductPublicModels } from '@features/products/product';
import { GridType } from '@shared/models/grid-type.model';
import { ProductMapPrices } from '@shared/prices/models/product-prices.model';
import { ProductMocacoService } from '@shared/product/services/product-mocaco.service';
import { getProductAttributesUseCase } from '@shared/product/services/product-utils.service';
import { isAllProductSizesNotBuyableUseCase } from '@shared/product/use-cases/product-detail-product.use-case';

@Component({
    selector: 'product-view-details',
    templateUrl: './product-view-details.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ProductViewColorSelectorComponent,
        ProductPricesComponent,
        TranslateModule,
        ProductPromotionDatesComponent,
        AsyncPipe,
        UpperCasePipe,
        NgClass,
        NgStyle,
    ],
    providers: [ProductMocacoService],
    standalone: true,
})
export class ProductViewDetailsComponent implements OnInit {
    @Input() public product: ProductPublicModels.PublicProduct;

    @Input() public templateConfiguration?: ProductViewConfig;

    @Input() public priceInfo: ProductMapPrices;

    @Input() public categoryConfig: CategoryConfiguration;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() public readonly onClick: EventEmitter<any> = new EventEmitter();

    @Input() public gridType: GridType;

    @Input() public colorStyle?: string;

    public hasBundle: boolean;

    public classSublineInBundle = '';

    public colors: any = [];

    public productDescriptions: ProductPublicModels.PublicProductAttribute[] = [];

    public hasAttrMostrarPrecioTotal = false;

    public hasAttrMostrarRango = false;

    public tag: boolean;

    public attributesInMocaco$: Observable<MocacoAttributesPublicModels.PublicMocacoAttribute[]>;

    public readonly productAttributeEnum = MocacoAttributesPublicConstants.MocacoAttributesEnum;

    public readonly isRtl$$: Signal<boolean> = this.store.selectSignal(getLanguageisRTL);

    private readonly colors$: BehaviorSubject<ProductPublicModels.PublicProductColor[]> = new BehaviorSubject([]);

    public isBrowser: boolean = this.platform.isBrowser();

    public showColors = false;

    public showPrices = false;

    public configDays$: Observable<number> = this.store.select(getCmsConfigValue('new_label_days'));

    private readonly MAX_ATTRIBUTE_TO_SHOW: number = 2;

    constructor(
        private readonly platform: CorePlatformService,
        private readonly store: Store,
        private readonly productMocacoService: ProductMocacoService,
    ) { }

    public ngOnInit(): void {
        this.hasBundle = this.product.gridElemType === 'BUNDLE'; // esto debería venir transformado del Product
        this.colors = this.product.colors.filter((color) => isAllProductSizesNotBuyableUseCase(color.sizes, ['HIDDEN'], false));
        this.showColors = this.colors?.length > 1 && !this.hasBundle && this.templateConfiguration?.showColors;
        this.showPrices = !this.hasBundle && this.templateConfiguration?.showPrice && !!this.priceInfo;

        for (const attribute of this.product.attributes) {
            if (attribute.type === 'DESCRIPTION') {
                this.productDescriptions.push(attribute);
            }
            if (attribute.name === 'mostrar_precio_total') {
                this.hasAttrMostrarPrecioTotal = true;
            }
            if (attribute.name === 'mostrar_rango') {
                this.hasAttrMostrarRango = true;
            }
        }
        this.checkAttributes();

        if (this.product.metadata?.labels) {
            this.tag = this.product.metadata.labels.some((label) => label.value === 'LABEL_NEW');
        }
        if (this.hasBundle && this.templateConfiguration?.showLinesInDetails) {
            this.classSublineInBundle = 'border-b2';
        }

        if (!this.colorStyle) {
            this.colorStyle = this.templateConfiguration?.styles?.fontColor;
        }
    }

    public onClickButton(): boolean {
        this.onClick.emit({ typeOfClick: 'default' });
        return false;
    }

    private checkAttributes() {
        this.attributesInMocaco$ = combineLatest([
            this.configDays$,
            this.productMocacoService.getProductMocacoAttributes(
                this.product,
                this.categoryConfig?.category?.id,
                this.templateConfiguration.location
            ),
        ]).pipe(
            map(([configDays, mocacoAttrs]) => {
                return getProductAttributesUseCase(
                    this.product,
                    mocacoAttrs,
                    configDays,
                    this.categoryConfig?.isCategoryNew
                )?.slice(0, this.MAX_ATTRIBUTE_TO_SHOW);
            })
        );
    }
}
